/** Live sign-ups for the Growth board (dream-be lib/growthSignupEvents.ts). */

export const GROWTH_SIGNUP_EVENT = 'growth-signup';

/** How long the globe stays up after the last sign-up before the chart returns. */
export const GLOBE_HOLD_MS = 2 * 60 * 1000;

export const MAX_FEED = 8;

/** Polling fallback for when the Pusher connection is asleep or dropped. */
export const POLL_MS = 30 * 1000;

export interface GrowthSignup {
  sessionId: string;
  completedAt: string;
  location: string | null;
  lat: number | null;
  lng: number | null;
  source: string;
  sourceLabel: string;
}

/** Newest first, de-duplicated by session, capped. */
export const addSignup = (
  list: GrowthSignup[],
  signup: GrowthSignup,
  max = MAX_FEED,
): GrowthSignup[] =>
  [signup, ...list.filter((s) => s.sessionId !== signup.sessionId)].slice(
    0,
    max,
  );

/** `arrivals` oldest first, so the newest ends up on top. */
export const addSignups = (
  list: GrowthSignup[],
  arrivals: GrowthSignup[],
): GrowthSignup[] => arrivals.reduce((acc, s) => addSignup(acc, s), list);

/** The globe shows while a sign-up arrived within GLOBE_HOLD_MS of `now`. */
export const globeActive = (
  lastSignupAt: number | null,
  now: number,
): boolean => lastSignupAt != null && now - lastSignupAt < GLOBE_HOLD_MS;

export const signupPlace = (s: GrowthSignup): string =>
  s.location?.trim() || 'Somewhere on Earth';

/**
 * Sign-ups in `fetched` the board hasn't seen: not already in `known` and
 * completed after the newest known one (so the capped feed's older entries
 * are never mistaken for new). Oldest first.
 */
export const unseenSignups = (
  known: GrowthSignup[],
  fetched: GrowthSignup[],
): GrowthSignup[] => {
  const ids = new Set(known.map((s) => s.sessionId));
  const newest = known.reduce<string | null>(
    (acc, s) => (acc == null || s.completedAt > acc ? s.completedAt : acc),
    null,
  );
  return fetched
    .filter(
      (s) =>
        !ids.has(s.sessionId) && (newest == null || s.completedAt > newest),
    )
    .sort((a, b) => a.completedAt.localeCompare(b.completedAt));
};
